import { Children, ComponentPropsWithoutRef, forwardRef, Fragment, ReactNode } from 'react';
import styled from 'styled-components';
import { fontSize, fontWeight } from 'styled-system';

import { Tooltip } from '../../cdl/Tooltip/Tooltip';
import { theme } from '../ui/theme';

const StyledTextButtonContent = styled.span({
    overflow: 'visible',
    position: 'relative', // this is required in Safari for overflow visible to work
    display: 'flex',
    alignItems: 'center',
});

const StyledTextWrapper = styled.span({
    textAlign: 'left',
});

const StyledElementWrapper = styled.span({
    '&:first-child': {
        marginRight: '4px',
    },
    '&:last-child': {
        marginLeft: '4px',
    },
    '&:only-child': {
        marginLeft: 0,
        marginRight: 0,
    },
});

const StyledTextButton = styled.button(
    {
        fontWeight: 400,
        border: 'none',
        outline: 'none',
        cursor: 'pointer',
        color: theme.colors.accent.foreground,
        padding: 0,
        position: 'relative',
        boxSizing: 'border-box',
        '&:active': {
            color: theme.colors.accent.foreground,
        },
        '&:disabled': {
            color: theme.colors.foreground.disabled,
            textDecoration: 'none',
        },
    },
    fontWeight,
    fontSize
);

interface TextButtonProps extends ComponentPropsWithoutRef<'button'> {
    tooltip?: ReactNode;
    children: ReactNode;
}

export const TextButton = forwardRef<HTMLButtonElement, TextButtonProps>(({ tooltip, children, ...props }, ref) => {
    const buttonProps: ComponentPropsWithoutRef<'button'> = { type: 'button', ...props };
    const content = (
        <Fragment>
            {Children.map(children, (child) => {
                return typeof child === 'string' ? (
                    <StyledTextWrapper>{child}</StyledTextWrapper>
                ) : (
                    <StyledElementWrapper>{child}</StyledElementWrapper>
                );
            })}
        </Fragment>
    );

    if (!tooltip) {
        return (
            <StyledTextButton ref={ref} {...buttonProps}>
                <StyledTextButtonContent>{content}</StyledTextButtonContent>
            </StyledTextButton>
        );
    }

    return (
        <Tooltip label={tooltip}>
            <StyledTextButton ref={ref} {...buttonProps}>
                <StyledTextButtonContent>{content}</StyledTextButtonContent>
            </StyledTextButton>
        </Tooltip>
    );
});

TextButton.displayName = 'TextButton';
